/* eslint-disable prettier/prettier */
/* eslint-disable no-nested-ternary */
import React, { useState, useMemo, useEffect } from "react"
import { useQuery } from "react-query"
import { useNavigate } from "react-router-dom"
import {
	TextField,
	Autocomplete,
	Button,
	Typography,
	Box,
	CircularProgress,
} from "@mui/material"
import userService from "../../api/services/userService"
import cvskillService from "../../api/services/cvskillService"
import User from "../../api/models/user"
import CvSkillDto from "../../api/models/cvskill"

function CvSkillHome() {
	const [searchInput, setSearchInput] = useState("")
	const [selectedUser, setSelectedUser] = useState<User | null>(null)
	const navigate = useNavigate()

	const { data: users, isLoading: isLoadingUsers } = useQuery<User[], Error>(
		"allUsers",
		() => userService.getAllUsers(),
	)

	const filteredUsers = useMemo(() => {
		if (!users || !Array.isArray(users) || searchInput.length < 4) return []
		const res = users.filter(
			(user: User) =>
				user &&
				typeof user.firstName === "string" &&
				typeof user.name === "string" &&
				`${user.firstName} ${user.name}`
					.toLowerCase()
					.includes(searchInput.toLowerCase()),
		)
		console.log("[FILTERED_USERS]", { searchInput, count: res.length })
		return res
	}, [users, searchInput])

	const fetchCvSkill = async (userId: number): Promise<CvSkillDto | null> => {
		console.log("[FETCH_CV_SKILL] by userId =", userId)
		try {
			const data = await cvskillService.getCvSkillsByUserId(userId)
			console.log("[FETCH_CV_SKILL][OK]", data)
			return data
		} catch (error) {
			console.log("[FETCH_CV_SKILL] No CV Skill found for user", userId)
			return null
		}
	}

	const { data: cvSkill, isLoading: isLoadingCvSkill } = useQuery<
		CvSkillDto | null,
		Error
	>(
		["cvSkill", selectedUser?.id],
		() =>
			selectedUser?.id
				? fetchCvSkill(Number(selectedUser.id))
				: Promise.resolve(null),
		{
			enabled: !!selectedUser?.id,
			retry: false,
			refetchOnWindowFocus: false,
		},
	)

	useEffect(() => {
		console.log("[SELECTED_USER_CHANGED]", selectedUser)
	}, [selectedUser])

	useEffect(() => {
		console.log("[CV_SKILL_FROM_QUERY]", cvSkill)
	}, [cvSkill])

	/**
	 * 🧹 Fonction pour nettoyer TOUT le localStorage des données CV
	 * Supprime toutes les clés liées aux CV Skills précédents
	 */
	const clearAllCvSkillData = () => {
		console.log("🧹 [CLEANUP] Nettoyage du localStorage des anciens CV...")

		// Liste de TOUTES les clés à supprimer
		const keysToRemove = [
			"selectedUserId",
			"selectedCvSkillId",
			// Données de personnalité
			"selectedPersonalityTraits",
			"selectedPersonalityType",
			"selectedAssociatedTraits",
			// Données d'atouts
			"selectedAtouts",
			// Données d'intérêts
			"selectedInterests",
			"poleLoisirInterets",
		]

		// Supprimer toutes les clés génériques
		keysToRemove.forEach(key => {
			localStorage.removeItem(key)
		})

		// Supprimer toutes les clés avec suffixe userId (ex: selectedAtouts-123)
		const allKeys = Object.keys(localStorage)
		const cvSkillRelatedKeys = allKeys.filter(
			key =>
				key.includes("selectedPersonalityTraits-") ||
				key.includes("selectedPersonalityType-") ||
				key.includes("selectedAssociatedTraits-") ||
				key.includes("selectedAtouts-") ||
				key.includes("selectedInterests-") ||
				key.includes("poleLoisirInterets-") ||
				key.includes("cvSkill-"),
		)

		cvSkillRelatedKeys.forEach(key => {
			console.log(`  ❌ Suppression de: ${key}`)
			localStorage.removeItem(key)
		})

		console.log(
			"✅ [CLEANUP] Nettoyage terminé - localStorage est propre!",
		)
	}

	/**
	 * Gestion de la sélection d'un utilisateur
	 * Nettoie TOUT le localStorage avant de sélectionner le nouvel utilisateur
	 */
	const handleUserSelect = (user: User | null) => {
		console.log("[HANDLE_SELECT_USER]", user)

		// 🧹 NETTOYER TOUT LE LOCALSTORAGE DES ANCIENS CV
		clearAllCvSkillData()

		// Définir le nouvel utilisateur
		setSelectedUser(user)

		// Sauvegarder uniquement le nouvel userId si présent
		if (user?.id) {
			localStorage.setItem("selectedUserId", String(user.id))
			console.log(
				`✅ [STORAGE] Nouvel utilisateur sélectionné: ${user.id}`,
			)
		}
	}

	const handleViewCvSkill = () => {
		console.log("[CLICK] Voir le CV Skill", {
			selectedUserId: selectedUser?.id,
			cvSkillId: cvSkill?.id,
		})
		if (selectedUser?.id && cvSkill?.id) {
			navigate("/cvskill/cv", {
				state: {
					userId: Number(selectedUser.id),
					cvSkillId: Number(cvSkill.id),
				},
			})
			localStorage.setItem("selectedUserId", String(selectedUser.id))
			localStorage.setItem("selectedCvSkillId", String(cvSkill.id))
		} else {
			console.warn("[CLICK][WARN] userId ou cvSkillId manquant.")
		}
	}

	const handleCreateCvSkill = () => {
		console.log("[CLICK] Créer un CV Skill", {
			selectedUserId: selectedUser?.id,
		})
		if (selectedUser?.id) {
			navigate("/cvSkill/pageAcceuil", {
				state: { userId: Number(selectedUser.id) },
			})
			localStorage.setItem("selectedUserId", String(selectedUser.id))
		} else {
			console.warn("[CLICK][WARN] Aucun utilisateur sélectionné.")
		}
	}

	return (
		<Box className="container mx-auto p-6 max-w-2xl">
			<style>{`
				.page-header {
					background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
					color: white;
					padding: 24px;
					border-radius: 16px;
					text-align: center;
					box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
					margin-bottom: 32px;
				}
				
				.page-header h2 {
					font-size: 28px;
					font-weight: 700;
					margin: 0;
				}
				
				.page-header p {
					font-size: 14px;
					opacity: 0.9;
					margin-top: 8px;
				}
				
				.search-container {
					background: white;
					padding: 24px;
					border-radius: 16px;
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
					margin-bottom: 24px;
				}
				
				.MuiAutocomplete-root .MuiOutlinedInput-root {
					border-radius: 12px;
				}
				
				.MuiAutocomplete-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
					border-color: #2daf8e;
					border-width: 2px;
				}
				
				.MuiAutocomplete-root .MuiInputLabel-root.Mui-focused {
					color: #2daf8e;
				}
				
				.user-card {
					background: linear-gradient(135deg, #d4f5ed 0%, #b8ede0 100%);
					border: 2px solid #2daf8e;
					border-radius: 16px;
					padding: 24px;
					box-shadow: 0 4px 12px rgba(45, 175, 142, 0.2);
					margin-top: 24px;
				}
				
				.user-info {
					display: flex;
					align-items: center;
					gap: 16px;
					margin-bottom: 20px;
				}
				
				.user-avatar {
					width: 60px;
					height: 60px;
					background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 28px;
					color: white;
					font-weight: 700;
				}
				
				.user-details h6 {
					font-size: 20px;
					font-weight: 700;
					color: #1f2937;
					margin: 0;
				}
				
				.user-details p {
					font-size: 14px;
					color: #6b7280;
					margin: 4px 0 0 0;
				}
				
				.action-button {
					background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%) !important;
					box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3) !important;
					text-transform: none !important;
					font-size: 16px !important;
					font-weight: 600 !important;
					padding: 12px 24px !important;
					border-radius: 12px !important;
					width: 100%;
					transition: all 0.3s ease !important;
				}
				
				.action-button:hover {
					transform: translateY(-2px);
					box-shadow: 0 6px 16px rgba(45, 175, 142, 0.4) !important;
				}
				
				.action-button-secondary {
					background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
					box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3) !important;
					text-transform: none !important;
					font-size: 16px !important;
					font-weight: 600 !important;
					padding: 12px 24px !important;
					border-radius: 12px !important;
					width: 100%;
					transition: all 0.3s ease !important;
				}
				
				.action-button-secondary:hover {
					transform: translateY(-2px);
					box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4) !important;
				}
				
				.loading-container {
					display: flex;
					justify-content: center;
					padding: 20px;
				}
				
				.MuiCircularProgress-root {
					color: #2daf8e !important;
				}
			`}</style>

			<div className="page-header">
				<h2>🔍 Rechercher un CV Skill</h2>
				<p>Trouvez et gérez les CV Skills de vos utilisateurs</p>
			</div>

			<div className="search-container">
				<Autocomplete
					options={filteredUsers}
					value={selectedUser}
					inputValue={searchInput}
					onInputChange={(_, val) => {
						setSearchInput(val)
						console.log("[INPUT_CHANGE]", val)
					}}
					onChange={(_, newValue) => handleUserSelect(newValue)}
					isOptionEqualToValue={(opt, val) =>
						(opt?.id ?? 0) === (val?.id ?? 1)
					}
					getOptionLabel={(option: User) =>
						option
							? `${option.firstName || ""} ${option.name || ""}`.trim()
							: ""
					}
					renderInput={params => (
						<TextField
							{...params}
							label="🔎 Rechercher un utilisateur"
							placeholder="Entrez au moins 4 caractères..."
						/>
					)}
					loading={isLoadingUsers}
					noOptionsText={
						searchInput.length < 4
							? "Entrez au moins 4 caractères pour rechercher"
							: "Aucun utilisateur trouvé"
					}
				/>
			</div>

			{selectedUser && (
				<div className="user-card">
					<div className="user-info">
						<div className="user-avatar">
							{selectedUser.firstName?.[0]?.toUpperCase() || "?"}
						</div>
						<div className="user-details">
							<Typography component="h6">
								{selectedUser.firstName} {selectedUser.name}
							</Typography>
							<Typography component="p">
								{selectedUser.email}
							</Typography>
						</div>
					</div>

					{isLoadingCvSkill ? (
						<div className="loading-container">
							<CircularProgress size={40} />
						</div>
					) : cvSkill ? (
						<Button
							variant="contained"
							className="action-button"
							onClick={handleViewCvSkill}
						>
							📄 Voir le CV Skill existant
						</Button>
					) : (
						<Button
							variant="contained"
							className="action-button-secondary"
							onClick={handleCreateCvSkill}
						>
							✨ Créer un nouveau CV Skill
						</Button>
					)}
				</div>
			)}
		</Box>
	)
}

export default CvSkillHome